import { useEffect, useState, type FormEvent } from 'react';
import type { ReviewOrganization } from '@urbanik/api-client';
import { CanonicalCard, TextField } from '@urbanik/ui';
import { reviewApiBase } from './api-base';
const member = '30000000-0000-4000-8000-000000000001';
type Item = {
  definitionId: string;
  code: string;
  title: string;
  definitionVersion: number;
  questionId: string;
  prompt: string;
  responseId: string | null;
  status: string | null;
  version: number | null;
  periodStart: string | null;
  answer: string | null;
  updatedAt: string | null;
};
async function call<T>(org: string, init?: RequestInit) {
  const response = await fetch(
      `${reviewApiBase}/identity/multi-review/organizations/${org}/members/${member}/questionnaires`,
      {
        ...init,
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'x-urbanik-review': '1',
        },
      },
    ),
    body = await response.json().catch(() => null);
  if (!response.ok) throw new Error(body?.message ?? 'No pudimos completar la solicitud.');
  return body as T;
}
export function EntityQuestionnaireReviewPanel({
  organization,
}: {
  organization: ReviewOrganization;
}) {
  const [item, setItem] = useState<Item | null>(null),
    [answer, setAnswer] = useState(''),
    [reason, setReason] = useState('Registro ficticio para validar cuestionario por entidad'),
    [period, setPeriod] = useState('2097-01-01'),
    [error, setError] = useState(''),
    [success, setSuccess] = useState(''),
    [saving, setSaving] = useState(false);
  useEffect(() => {
    setItem(null);
    setError('');
    void call<{ items: Item[] }>(organization.id)
      .then(({ items }) => {
        const first = items[0] ?? null;
        setItem(first);
        setAnswer(first?.answer ?? '');
        setPeriod(first?.periodStart ?? '2097-01-01');
      })
      .catch((failure) =>
        setError(failure instanceof Error ? failure.message : 'Error inesperado.'),
      );
  }, [organization.id]);
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!item) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const result = await call<{ items: Item[] }>(organization.id, {
          method: 'PUT',
          body: JSON.stringify({
            definitionId: item.definitionId,
            questionId: item.questionId,
            responseId: item.responseId,
            expectedVersion: item.version,
            periodStart: period,
            answer,
            reason,
          }),
        }),
        next = result.items[0] ?? null;
      setItem(next);
      setAnswer(next?.answer ?? '');
      setSuccess(`Respuesta guardada únicamente en ${organization.code}.`);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Error inesperado.');
    } finally {
      setSaving(false);
    }
  }
  return (
    <section aria-label={'Cuestionario de ' + organization.code}>
      <h2>Cuestionario por entidad</h2>
      <p>
        El formulario <strong>SOCIO_BASE</strong> puede existir con el mismo código en A y B, pero
        sus respuestas y versiones son independientes.
      </p>
      {error ? <p role="alert">{error}</p> : null}
      {success ? <p role="status">{success}</p> : null}
      {!item && !error ? (
        <p role="status">Cargando cuestionario…</p>
      ) : item ? (
        <CanonicalCard className="entity-review-card">
          <h3>{item.title}</h3>
          <p>
            Estado: {item.status ?? 'sin respuesta'} · Versión de respuesta: {item.version ?? 0}
          </p>
          <form onSubmit={(event) => void save(event)}>
            <TextField
              id={'questionnaire-period-' + organization.id}
              label="Inicio del período"
              type="date"
              required
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
            />
            <label>
              {item.prompt}
              <textarea
                required
                maxLength={1000}
                value={answer}
                onChange={(event) => setAnswer(event.target.value)}
              />
            </label>
            <TextField
              id={'questionnaire-reason-' + organization.id}
              label="Motivo obligatorio (mínimo 10 caracteres)"
              required
              minLength={10}
              maxLength={500}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <button
              className="ui-button ui-button--primary"
              disabled={saving || !answer.trim() || reason.trim().length < 10}
            >
              {saving
                ? 'Guardando…'
                : item.responseId
                  ? 'Guardar nueva versión'
                  : 'Registrar respuesta en ' + organization.code}
            </button>
          </form>
        </CanonicalCard>
      ) : null}
    </section>
  );
}
